(function (root) {
  const OPEN_PARAM = 'alac_cart';

  // Shopify codes are case-insensitive; trimmed and upper-cased so "save10 " and "SAVE10" match.
  function normalizeCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  // Only same-site paths are allowed as the return target.
  function safePath(path, fallback) {
    const text = String(path || '');
    return /^\/(?![/\\])/.test(text) ? text : fallback;
  }

  function rootPath(rootUrl) {
    return safePath(rootUrl, '/').replace(/\/?$/, '/');
  }

  // Current page plus a flag that re-opens the cart drawer after the discount redirect.
  function withDrawerOpen(pathname, search) {
    const params = new URLSearchParams(search || '');
    params.set(OPEN_PARAM, 'open');
    return safePath(pathname, '/') + '?' + params.toString();
  }

  // The /discount/CODE route, kept only as the fallback when the Cart API cannot be reached.
  function discountUrl(code, redirect, rootUrl) {
    const clean = normalizeCode(code);
    if (!clean) return null;
    return rootPath(rootUrl) + 'discount/' + encodeURIComponent(clean) + '?redirect=' + encodeURIComponent(safePath(redirect, '/cart'));
  }

  // Returns the search string without the drawer flag, or null when the flag is absent.
  function consumeOpenFlag(search) {
    const params = new URLSearchParams(search || '');
    if (params.get(OPEN_PARAM) !== 'open') return null;
    params.delete(OPEN_PARAM);
    const rest = params.toString();
    return rest ? '?' + rest : '';
  }

  // Codes already on the cart, as /cart.js lists them.
  function cartCodes(cart) {
    return ((cart && cart.discount_codes) || []).map(entry => normalizeCode(entry && entry.code)).filter(Boolean);
  }

  // /cart/update.js replaces the cart's codes with the list it is given, so a new code is added to the
  // ones already there (no duplicates) and a removed code is left out of the rest.
  function withCode(codes, code) {
    const clean = normalizeCode(code);
    const list = (codes || []).map(normalizeCode).filter(item => item && item !== clean);
    return clean ? list.concat(clean) : list;
  }

  function withoutCode(codes, code) {
    const clean = normalizeCode(code);
    return (codes || []).map(normalizeCode).filter(item => item && item !== clean);
  }

  // 'applied' when /cart.js lists the code as applicable; anything else (unknown, expired, not for these
  // items or below a minimum) is 'not_applicable', since the Cart API does not say which.
  function codeStatus(cart, code) {
    const clean = normalizeCode(code);
    const entry = ((cart && cart.discount_codes) || []).find(item => normalizeCode(item && item.code) === clean);
    return entry && entry.applicable ? 'applied' : 'not_applicable';
  }

  function format(template, code) {
    return String(template || '').replace(/\[code\]/g, code);
  }

  const api = {
    normalizeCode,
    discountUrl,
    withDrawerOpen,
    consumeOpenFlag,
    cartCodes,
    withCode,
    withoutCode,
    codeStatus,
    OPEN_PARAM,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.customElements || root.customElements.get('alac-discount')) return;
  root.AlacCartDiscount = api;

  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  function getCart(base) {
    return root.fetch(base + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(response => {
      if (!response.ok) throw new Error('cart ' + response.status);
      return response.json();
    });
  }

  // Where the field lives decides which sections come back with the update and how they are swapped in.
  function surfaceOf(host) {
    const drawer = host.closest('cart-drawer');
    if (drawer) return { kind: 'drawer', drawer, sections: ['cart-drawer', 'cart-icon-bubble'] };
    const items = root.document.getElementById('main-cart-items');
    const footer = root.document.getElementById('main-cart-footer');
    if (items && footer && items.dataset.id && footer.dataset.id) {
      return { kind: 'page', items, footer, sections: [items.dataset.id, footer.dataset.id, 'cart-icon-bubble'] };
    }
    return { kind: 'none', sections: [] };
  }

  function postCodes(base, codes, surface) {
    const body = { discount: codes.join(',') };
    if (surface.sections.length) {
      body.sections = surface.sections.join(',');
      body.sections_url = root.location.pathname;
    }
    return root.fetch(base + 'cart/update.js', { method: 'POST', credentials: 'same-origin', headers: jsonHeaders, body: JSON.stringify(body) }).then(response =>
      response.json().then(data => {
        if (!response.ok) throw new Error((data && (data.description || data.message)) || 'update ' + response.status);
        return data;
      })
    );
  }

  function inner(html, selector) {
    const node = new root.DOMParser().parseFromString(html || '', 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  // Swap in the fresh drawer or cart page; the drawer stays open (renderContents opens it again).
  function render(surface, data) {
    const sections = data && data.sections;
    if (!sections) return false;
    if (surface.kind === 'drawer') {
      surface.drawer.classList.toggle('is-empty', data.item_count === 0);
      surface.drawer.renderContents(data);
    } else if (surface.kind === 'page') {
      [[surface.items, surface.items.dataset.id], [surface.footer, surface.footer.dataset.id]].forEach(([element, id]) => {
        const target = element.querySelector('.js-contents');
        const html = inner(sections[id], '.js-contents');
        if (target && html !== null) target.innerHTML = html;
      });
      const bubble = root.document.getElementById('cart-icon-bubble');
      const bubbleHtml = inner(sections['cart-icon-bubble'], '.shopify-section');
      if (bubble && bubbleHtml !== null) bubble.innerHTML = bubbleHtml;
      if (root.Shopify && root.Shopify.PaymentButton && root.Shopify.PaymentButton.init) {
        try { root.Shopify.PaymentButton.init(); } catch (error) { /* wallets stay as rendered */ }
      }
    } else {
      return false;
    }
    // Other listeners still hear about the change. Source 'cart-items' because the cart's own item lists
    // skip it: they were just rendered from this response. PUB_SUB_EVENTS is a global const, not on window.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', alacSource: 'discount', cartData: data });
    }
    return true;
  }

  // The field is re-rendered with the cart, so feedback is written into the element that is live now.
  function feedback(inputId, kind, text, code) {
    const input = root.document.getElementById(inputId);
    const host = input ? input.closest('alac-discount') : null;
    if (!host) return;
    const details = host.querySelector('[data-alac-discount-details]');
    const message = host.querySelector('.alac-discount__message');
    const status = host.querySelector('.alac-discount__status');
    if (kind === 'error') {
      if (details) details.open = true;
      if (status) status.textContent = '';
      if (message) message.textContent = text;
      input.setAttribute('aria-invalid', 'true');
      holdFocus(input, host, true);
    } else {
      if (details) details.open = false;
      if (message) message.textContent = '';
      input.removeAttribute('aria-invalid');
      if (status) {
        status.textContent = '';
        root.setTimeout(() => { status.textContent = text; }, 50);
      }
      // When the list above the field already names the code, the status line is for screen readers only.
      const listed = !!code && [...host.querySelectorAll('.alac-discount__code')].some(item => item.textContent.toUpperCase().includes(code));
      if (status) status.classList.toggle('visually-hidden', listed);
      const summary = host.querySelector('.alac-discount__summary');
      if (summary) holdFocus(summary, host, false);
    }
  }

  // The drawer's re-render calls Dawn's open(), which waits for the drawer's next transitionend (often the
  // express buttons finishing loading) and then moves focus to the drawer panel. Focus goes back to the
  // field or the toggle right after that, and a few times while the drawer settles.
  function holdFocus(target, host, select) {
    const put = () => {
      if (!target.isConnected || host.contains(root.document.activeElement)) return;
      target.focus({ preventScroll: true });
      if (select && target.select) target.select();
    };
    put();
    const drawer = host.closest('cart-drawer');
    if (drawer) {
      root.setTimeout(() => {
        drawer.addEventListener('transitionend', () => root.setTimeout(put, 0), { once: true });
      }, 0);
    }
    [150, 400, 800].forEach(delay => root.setTimeout(put, delay));
  }

  function setBusy(host, busy) {
    host.classList.toggle('is-busy', busy);
    host.querySelectorAll('button').forEach(button => {
      button.disabled = busy;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  function strings(host) {
    const data = host.dataset;
    return {
      empty: data.emptyMessage || 'Enter a discount code first.',
      applied: data.appliedMessage || 'Code [code] applied.',
      already: data.alreadyMessage || 'Code [code] is already applied.',
      invalid: data.invalidMessage || "That code doesn't apply to this cart.",
      removed: data.removedMessage || 'Code [code] removed.',
      failed: data.errorMessage || "We couldn't update your codes. Please try again.",
    };
  }

  function apply(host, form) {
    const input = form.querySelector('[name="discount"]');
    const message = form.querySelector('.alac-discount__message');
    const text = strings(host);
    const code = normalizeCode(input && input.value);
    if (!code) {
      const details = form.closest('details');
      if (details) details.open = true;
      if (message) message.textContent = text.empty;
      if (input) { input.setAttribute('aria-invalid', 'true'); input.focus(); }
      return Promise.resolve('empty');
    }
    input.value = code;
    if (message) message.textContent = '';
    const inputId = input.id;
    const base = rootPath(host.dataset.root);
    const surface = surfaceOf(host);
    if (!root.fetch || surface.kind === 'none') {
      root.location.assign(discountUrl(code, host.dataset.redirect || withDrawerOpen(root.location.pathname, root.location.search), host.dataset.root));
      return Promise.resolve('redirect');
    }
    setBusy(host, true);
    let before = [];
    return getCart(base)
      .then(cart => {
        before = cartCodes(cart);
        if (before.includes(code) && codeStatus(cart, code) === 'applied') return 'already';
        return postCodes(base, withCode(before, code), surface).then(added =>
          getCart(base).then(after => {
            if (codeStatus(after, code) === 'applied') {
              render(surface, added);
              return 'applied';
            }
            // Not usable on this cart: put the cart's codes back as they were, so nothing is stored silently.
            return postCodes(base, before, surface).then(restored => {
              render(surface, restored);
              return 'not_applicable';
            });
          })
        );
      })
      .then(result => {
        setBusy(host, false);
        if (result === 'applied') feedback(inputId, 'success', format(text.applied, code), code);
        else if (result === 'already') feedback(inputId, 'success', format(text.already, code), code);
        else feedback(inputId, 'error', text.invalid);
        return result;
      })
      .catch(() => {
        setBusy(host, false);
        feedback(inputId, 'error', text.failed);
        return 'failed';
      });
  }

  function remove(host, code) {
    const text = strings(host);
    const base = rootPath(host.dataset.root);
    const surface = surfaceOf(host);
    const input = host.querySelector('[name="discount"]');
    const inputId = input && input.id;
    if (!root.fetch || surface.kind === 'none') return Promise.resolve('unsupported');
    setBusy(host, true);
    return getCart(base)
      .then(cart => postCodes(base, withoutCode(cartCodes(cart), code), surface))
      .then(data => {
        render(surface, data);
        setBusy(host, false);
        feedback(inputId, 'success', format(text.removed, normalizeCode(code)), null);
        return 'removed';
      })
      .catch(() => {
        setBusy(host, false);
        feedback(inputId, 'error', text.failed);
        return 'failed';
      });
  }

  root.customElements.define('alac-discount', class extends root.HTMLElement {
    connectedCallback() {
      if (this.alacBound) return;
      this.alacBound = true;
      this.addEventListener('submit', event => {
        const form = event.target.closest('form');
        if (!form) return;
        event.preventDefault();
        if (this.classList.contains('is-busy')) return;
        apply(this, form);
      });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-alac-discount-remove]');
        if (!button || this.classList.contains('is-busy')) return;
        event.preventDefault();
        remove(this, button.dataset.alacDiscountRemove);
      });
    }
  });

  // Links built by the former redirect flow (?alac_cart=open) still open the drawer once.
  function reopenDrawer() {
    const rest = consumeOpenFlag(root.location.search);
    if (rest === null) return;
    try { root.history.replaceState(root.history.state, '', root.location.pathname + rest + root.location.hash); } catch (error) { /* keep the flag */ }
    root.customElements.whenDefined('cart-drawer').then(() => {
      const drawer = root.document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    });
  }
  reopenDrawer();
})(typeof window !== 'undefined' ? window : globalThis);
